import React, { useMemo, useState } from "react";
import { Search, Check, Minus } from "lucide-react";
import { SourcePill, plural } from "./ui";

const Checkbox = ({ checked, indeterminate = false }) => (
  <span
    aria-hidden="true"
    className={`w-4 h-4 rounded-[5px] flex items-center justify-center shrink-0 transition-colors ${
      checked || indeterminate ? "bg-[#00A6A6] ring-1 ring-[#00A6A6]" : "bg-white ring-1 ring-gray-300"
    }`}
  >
    {checked && <Check className="w-3 h-3 text-white" strokeWidth={3} />}
    {!checked && indeterminate && <Minus className="w-3 h-3 text-white" strokeWidth={3} />}
  </span>
);

// Searchable member-lab checklist. `selected` is a Set of lab ids. Each row shows
// the lab's current test source so the user sees what a change will replace.
// `listId` marks labs already on this list; others on a different list get a
// "moves from" hint.
const LabChecklist = ({ labs, selected, onChange, listId = null, disabled = false, maxHeight = "max-h-[46vh]" }) => {
  const [query, setQuery] = useState("");
  const q = query.trim().toLowerCase();
  const shown = useMemo(
    () => (q ? labs.filter((l) => `${l.brand_name} ${l.city || ""} ${l.code || ""}`.toLowerCase().includes(q)) : labs),
    [labs, q]
  );
  const shownSelected = shown.filter((l) => selected.has(l.id)).length;
  const allShown = shown.length > 0 && shownSelected === shown.length;

  const toggle = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onChange(next);
  };

  const toggleShown = () => {
    const next = new Set(selected);
    shown.forEach((l) => (allShown ? next.delete(l.id) : next.add(l.id)));
    onChange(next);
  };

  return (
    <div className="rounded-2xl border border-[#DCEAEA] bg-white overflow-hidden">
      <div className="p-3 border-b border-[#E9F2F2] flex items-center gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search labs, city or code…"
            className="w-full pl-9 pr-3 py-2 rounded-lg border border-gray-200 bg-gray-50 text-sm focus:ring-2 focus:ring-[#00A6A6]/50 focus:border-transparent"
          />
        </div>
        <button
          type="button"
          onClick={toggleShown}
          disabled={disabled || shown.length === 0}
          className="shrink-0 px-2.5 py-2 text-xs font-medium text-[#0F4C81] rounded-lg hover:bg-[#F1F8F8] disabled:opacity-40 transition-colors"
        >
          {allShown ? "Clear shown" : `Select ${q ? "shown" : "all"}`}
        </button>
      </div>

      <ul className={`${maxHeight} overflow-y-auto divide-y divide-gray-50`}>
        {shown.length === 0 ? (
          <li className="px-4 py-8 text-center text-sm text-gray-400">No labs match “{query}”.</li>
        ) : (
          shown.map((lab) => {
            const isOn = selected.has(lab.id);
            const onOtherList = lab.test_source === "chain" && lab.test_list?.id !== listId;
            return (
              <li key={lab.id}>
                <button
                  type="button"
                  role="checkbox"
                  aria-checked={isOn}
                  disabled={disabled}
                  onClick={() => toggle(lab.id)}
                  className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors ${isOn ? "bg-[#F3FBFB]" : "hover:bg-gray-50"} disabled:cursor-default`}
                >
                  <Checkbox checked={isOn} />
                  <span className="min-w-0 flex-1">
                    <span className="flex items-center gap-2">
                      <span className="text-sm font-medium text-gray-900 truncate">{lab.brand_name}</span>
                      {!lab.active && <span className="text-[10px] font-medium text-gray-400 uppercase">Inactive</span>}
                    </span>
                    <span className="block text-[11px] text-gray-400 truncate">
                      {[lab.city, lab.code].filter(Boolean).join(" · ") || "—"}
                    </span>
                  </span>
                  <span className="hidden sm:flex flex-col items-end gap-0.5 shrink-0 max-w-[45%]">
                    <SourcePill lab={lab} />
                    <span className="text-[11px] text-gray-400">
                      {plural(lab.test_count, "test")}
                      {isOn && onOtherList && <span className="text-amber-600"> · will move</span>}
                    </span>
                  </span>
                </button>
              </li>
            );
          })
        )}
      </ul>

      <div className="px-4 py-2 border-t border-[#E9F2F2] bg-[#FAFDFD] text-[11px] text-gray-500">
        {selected.size} of {plural(labs.length, "member lab")} selected
      </div>
    </div>
  );
};

export default LabChecklist;
